/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { invalidateAuditLogQueries } from "../../../utils/invalidateAuditLogQueries";
import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useRef,
  useCallback,
} from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Typography,
  Button,
  Select,
  Divider,
  Box,
  Checkbox,
  Menu,
  Breadcrumbs,
  IconButton,
  // useMediaQuery,
  // useTheme,
  InputAdornment,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useDropzone } from "react-dropzone";
import loadingImg from "../../../assets/images/loader2.gif";
import {
  fetchColumns,
  updateColumns,
  uploadFile,
} from "../../../api/orderService";
import camelize from "../../../utils/camelize";
import CachedIcon from "@mui/icons-material/Cached";
import snakeCase from "../../../utils/snakeCase";
import FilterSection from "../shared/FilterSection";
import DataTable from "../shared/DataTable";
import dayjs from "dayjs";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { AbilityContext } from "../../../contexts/abilityContext";
import { StyledBreadCrumbContainer, StyledLink } from "../../user_management/styles";
import { MenuHeading } from "../../../components/menu_heading";
import { useInfiniteQuery, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import api from "../../../api";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { checkAdmin } from "../../../utils/checkAdmin";
import { useSelector } from "react-redux";
import { strictCanScope } from "../../../utils/caslStrict";
import { LoadingSpinner } from "../../../components/loading_spinner";
import { useLocation } from "react-router-dom";
import { OrderAppliedDiscountModal } from "../../../components/order_discount_modal";
import { formatDate } from "../../../utils/formatDate";
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { matchesNormalizedSearch, normalizeSearchTerm } from '../../../utils/normalizeSearchTerm'
import csvloader from '../../../assets/images/csv2.gif'
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { Query_Keys } from "../../../lib/queryKeys";
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { useCsvExport } from "../../../hooks/exports/useCsvExport";
import { reportMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/reportMutationKeys";
import { OrderNumberCell } from "../../../components/order_number_cell";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  hasDisplayableDropdownValue,
  mergeOptionsIfChanged,
  normalizeEmployee,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import {
  perPageStyles,
  StyledTypography,
  ShowHideButton,
  StyledSearchBar,
} from "./styles";
// When requirement gets confirmed below code will be uncommented or removed
// import { OrderStatisticsCards } from "../../../components/order_statistics_cards";
// import { formatOrderTotalSalesData } from "../../../utils/formatOrderTotalSalesData";
// import { OrderTotalSalesComponent } from "../../../components/order_total_sales_component";
import { getDateRange } from "../../../utils/dateRangeHelper";

const EMPTY_DROPDOWN_OPTIONS = []

const ReportHomePage = () => {
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("desc");
  const [visibleColumns, setVisibleColumns] = useState({});
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingLoader, setFetchingLoader] = useState(false);
  const [fetchingDataLoader, setFetchingDataLoader] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState('')
  const { ability } = useContext(AbilityContext);
  const queryClient = useQueryClient();
  const defaultFilters = {
    view: "Sales",
    dateFilter: "Today",
    hours: "All Hours",
    employees: "All Employees",
    customDateFrom: null,
    customDateTo: null,
    customHoursFrom: null,
    customHoursTo: null,
    moreOptions: [],
    perqed: [],
    state: [],
    source: [],
    taxExempt: [],
    voided: [],
    restaurantIds: [],
    has_ot_discount: null,
    restaurantGroupId: '',
    restaurantSubGroupId: '',
    importedOrderIds: [],
  }
  const [queryFilter, setQueryFilter] = useState(defaultFilters)
  const previousVisibleColumnsRef = useRef(null)
  const showNotification = useNotificationContext();
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const [fetchPermission, setFetchPermission] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState('')
  const location = useLocation()
  let navigatedRestaurant = location?.state?.restaurant
  const navigatedOrderIds = location?.state?.importedOrderIds || []
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [page, setPage] = useState(1)
  // When requirement gets confirmed below code will be uncommented or removed
  // const theme = useTheme()
  // const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  
  const [search, setSearch] = useState("")
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([])
  const [employeeOptionsCache, setEmployeeOptionsCache] = useState([])
  const debouncedEmployeeSearch = useDebounce(employeeSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300)

  React.useEffect(() => {
    if(navigatedRestaurant?.name){
      setFilters(prevFilters => ({
        ...prevFilters,
        restaurantIds: [navigatedRestaurant?.id],
        dateFilter: 'Custom Date',
        customDateFrom: '',
        customDateTo: '',
        importedOrderIds: navigatedOrderIds,
      }));
      setQueryFilter({
        ...defaultFilters,
        restaurantIds: [navigatedRestaurant?.id],
        dateFilter: 'Custom Date',
        customDateFrom: '',
        customDateTo: '',
        importedOrderIds: navigatedOrderIds,
       })
      setFetchPermission(strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${navigatedRestaurant?.id}`))
      navigatedRestaurant = null
    }
  }, [])

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat(bytes / Math.pow(k, i)).toFixed(1) + " " + sizes[i];
  };

  // Add dropzone hook
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    onDrop: (acceptedFiles) => {
      const validFiles = acceptedFiles?.filter(
        (file) => file.size <= 900 * 1024 * 1024 // 900MB limit
      );
      setSelectedFiles(validFiles);
    },
  });

  // Main filters state
  const [filters, setFilters] = useState(defaultFilters);

  const moreOptionsConfig = [
    { key: "perqed", label: "Perqed", testId: "perqed-state-filter-chip" },
    { key: "state", label: "State", testId: "order-state-filter-chip" },
    { key: "source", label: "Source", testId: "order-source-filter-chip" },
    { key: "taxExempt", label: "Tax Exempt", testId: "tax-exempt-state-filter-chip" },
    { key: "voided", label: "Voided", testId: "voided-state-filter-chip" },
  ];

  const selectOptions = {
    SideMenuOptions: [
      "Sales",
      "Sales Summary",
      "Locations",
      "Menu",
      "Labor",
      "Accounting",
    ],
    Days: [
      "All",
      "Today",
      "Yesterday",
      "This Week",
      "Last 7 Days",
      "This Month",
      "Last Month",
      "Custom Date",
    ],
    Hours: [
      "All Hours",
      "8:00 AM - 4:59 PM",
      "4:00 PM - 12:00 AM",
      "Custom Hours",
    ],
    Perqed: ["Yes", "No"],
    State: ["Open", "Paid", "Closed"],
    Source: [
      "Online",
      "In Store",
      "API",
      "Kiosk",
      "Caller Id",
      "Grubhub",
      "Toast Pickup App",
      "Order & Pay",
      "Google",
      "Invoice",
      "Toast Local",
      "Branded Online Ordering",
      "Catering",
      "Catering Online Ordering",
      "Toast Tables",
    ],
    "Tax Exempt": ["Yes", "No"],
    Voided: ["Yes", "No"],
  };

  const getSortKeyFromColumn = (columnName) => {
    switch (columnName) {
      case "Order Id":
        return "order_id";
      case "Order #":
        return "order_no";
      case "# of Guests":
        return "no_of_guest";
      case "Perq Amount":
        return "discount_amount";
      case "Duration":
        return "duration";
      case "Checks":
        return "check_no";
      default:
        return snakeCase(columnName);
    }
  };

  function convertTo24Hour(time) {
    const [hours, minutes] = time.split(":");
    const period = time.slice(-2);
    let normalizedHours = parseInt(hours, 10);

    if (period === "PM" && normalizedHours !== 12) normalizedHours += 12;
    if (period === "AM" && normalizedHours === 12) normalizedHours = 0;

    return `${normalizedHours.toString().padStart(2, "0")}:${minutes.slice(0, 2)}`;
  }

  const getEmployeeName = (id) => {
    if (!id || id === 'All Employees') {
      return 'All Employees'
    }

    const employee = employeeOptionsCache?.find((option) => `${option?.value}` === `${id}`)

    return employee?.label || id
  }

  const formattedFilters = (sourceFilters = queryFilter) => {
    const params = {};
    
    const dateRange = getDateRange(sourceFilters, "DD/MM/YYYY");

    if (dateRange) {
      params.date_range = dateRange;
    }

    // Time range handling
    if (sourceFilters?.hours !== "All Hours" && sourceFilters.hours !== "Custom Hours") {
      const [from, formam, _, to, toam] = sourceFilters.hours.split(" ");
      params.time_range = {
        from: convertTo24Hour(`${from}${formam}`),
        to:
          convertTo24Hour(`${to}${toam}`) === "00:00"
            ? "23:59"
            : convertTo24Hour(`${to}${toam}`),
      };
    } else if (
      sourceFilters?.hours === "Custom Hours" &&
      isValidDayjsValue(sourceFilters.customHoursFrom) &&
      isValidDayjsValue(sourceFilters.customHoursTo)
    ) {
      params.time_range = {
        from: dayjs(sourceFilters.customHoursFrom).format("HH:mm"),
        to: dayjs(sourceFilters.customHoursTo).format("HH:mm"),
      };
    }

    if (sourceFilters?.employees !== "All Employees") {
      params.network_guest_name = getEmployeeName(sourceFilters.employees);
    }

    if (sourceFilters?.restaurantGroupId) {
      params.restaurant_group_id = sourceFilters.restaurantGroupId;
    }

    if (sourceFilters?.restaurantSubGroupId) {
      params.restaurant_sub_group_id = sourceFilters.restaurantSubGroupId;
    }

    if (sourceFilters?.restaurantIds?.length !== 0) {
      params.restaurant_ids = sourceFilters.restaurantIds;
    }

    if (sourceFilters?.importedOrderIds?.length !== 0) {
      params.order_ids = sourceFilters.importedOrderIds;
    }

    if (sourceFilters?.has_ot_discount) {
      params.has_ot_discount = sourceFilters.has_ot_discount
    }

    // Filter parameters
    const filterMapping = {
      perqed: "discounted",
      voided: "voided",
      taxExempt: "tax_exempted",
      source: "order_source",
      opened: "opened",
      closed: "closed",
      paid: "paid",
    };

    moreOptionsConfig.forEach(({ key }) => {
      const value = sourceFilters[key];
      if (value && value.length > 0) {
        // Skip if both true/false options are selected
      if (["perqed", "voided", "taxExempt"].includes(key)) {
          if (value.length === 2) return;
          params[filterMapping[key]] = value[0] === "Yes" ? "true" : "false";
          return;
        }

        if (key === "state") {
          if (value.includes("Open")) {
            params["opened"] = "true";
          }
          if (value.includes("Paid")) {
            params["paid"] = "true";
          }
          if (value.includes("Closed")) {
            params["closed"] = "true";
          }
          return;
        }
        params[filterMapping[key]] = value;
      }
    });

    return params;
  };

  // When requirement gets confirmed below code will be uncommented or removed
  // const formatAnalyticsFilters = (sourceFilters = queryFilter) => {
  //   const params = {};
  //
  //   const dateRange = getDateRange(sourceFilters);
  //
  //   if (dateRange) {
  //     params.time = {
  //       start_date: dateRange.from,
  //       end_date: dateRange.to,
  //     };
  //   }
  //
  //   if (sourceFilters?.restaurantGroupId) {
  //     params.scope = {
  //       ...params.scope,
  //       group_ids: [sourceFilters.restaurantGroupId],
  //     };
  //   }
  //
  //   if (sourceFilters?.restaurantIds?.length !== 0) {
  //     params.scope = {
  //       ...params.scope,
  //       restaurant_ids: sourceFilters.restaurantIds,
  //     };
  //   }
  //
  //   return params;
  // }

  const appliedOrderFilters = useMemo(() => formattedFilters(queryFilter), [queryFilter]);

  // When requirement gets confirmed below code will be uncommented or removed
  // const appliedAnalyticsFilters = useMemo(
  //   () => formatAnalyticsFilters(queryFilter),
  //   [queryFilter]
  // );
  //
  // const hasAnalyticsDateRange = Boolean(
  //   appliedAnalyticsFilters?.time?.start_date && appliedAnalyticsFilters?.time?.end_date
  // );
  const normalizedSearchTerm = useMemo(() => normalizeSearchTerm(search), [search]);
  const debouncedSearchTerm = useDebounce(normalizedSearchTerm, 300);

  const fetchOrdersList = async () => {
    const data = appliedOrderFilters
    const sortBy = orderBy ? getSortKeyFromColumn(orderBy) : undefined;
    const requestPayload = {
      ...data,
      search: debouncedSearchTerm,
      sort_by: sortBy,
      sort_order: order,
      per_page: itemsPerPage,
      page: page
    };

    const response = queryFilter?.importedOrderIds?.length > 0
      ? await api.post('/v1/admin/orders/search', requestPayload)
      : await api.get('/v1/admin/orders', { params: requestPayload });

    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const {
    data: orders = [],
    isFetching: ordersLoading,
    refetch: fetchOrdersData,
  } = useQuery({
    queryKey: Query_Keys.orders({
      query_filter: queryFilter,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy: orderBy,
      sortOrder: order,
      search: debouncedSearchTerm,
    }),
    queryFn: fetchOrdersList,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchOrderAppliedDiscount = async () => {
    const response = await api.get(`/v1/admin/orders/${selectedOrder}/detail`)
    return response?.data
  }

  const { data: orderData, isLoading: appliedDiscountLoading } = useQuery({
    queryKey: Query_Keys.applied_discount({
      selected_order: selectedOrder
    }),
    queryFn: fetchOrderAppliedDiscount,
    staleTime: 5 * 60 * 1000,
    enabled: selectedOrder !== ''
  })

  // When requirement gets confirmed below code will be uncommented or removed
  // const fetchTotalSales = async () => {
  //   let res = await api.post('/v1/admin/dashboard/sales/query', { ...appliedAnalyticsFilters }, { 'axios-retry': { retries: 0 } })
  //   res = formatOrderTotalSalesData(res?.data?.timeseries?.points , res?.data?.data, appliedAnalyticsFilters?.scope?.restaurant_ids)
  //   return res
  // }
  //
  // const { data: orderAnalyticsData, isFetching: orderAnalyticsLoading } = useQuery({
  //   queryKey: Query_Keys.dashboard_total_sales({
  //     queryFilter: appliedAnalyticsFilters,
  //   }),
  //   queryFn: fetchTotalSales,
  //   staleTime: 10 * 60 * 1000,
  //   enabled: hasAnalyticsDateRange,
  //   placeholderData: keepPreviousData
  // })

  const sortedOrders = useMemo(() => orders?.data || [], [orders?.data]);
  const ordersPageCount = Number(orders?.pageCount || 0);

  const handleSearch = useCallback((searchQuery) => {
    setSearch(searchQuery);
    setPage(1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadColumns = async () => {
      try {
        setFetchingDataLoader(true);
        const res = await fetchColumns({ table_name: "orders" });
        if (isMounted) {
          updateColumnToggle(res);
        }
      } catch (error) {
        console.error('Failed to fetch columns:', error);
      } finally {
        if (isMounted) {
          setFetchingDataLoader(false);
        }
      }
    };

    loadColumns();

    return () => {
      isMounted = false;
    };
  }, [])

  const fetchEmployeeList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/active_guests_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedEmployeeSearch || undefined,
        restaurant_id: filters?.restaurantIds,
        restaurant_group_id: filters?.restaurantGroupId || undefined,
        restaurant_sub_group_id: filters?.restaurantSubGroupId || undefined
      }
    })
    return response?.data
      ?.map(normalizeEmployee)
      ?.filter((employee) => (
        hasDisplayableDropdownValue(employee?.label)
        && hasDisplayableDropdownValue(employee?.value)
      )) || []
  }

  const {
    data: employees = [],
    isFetching: employeeFetching,
    isFetchingNextPage: employeesFetchingNextPage,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.active_guests_list({
      restaurant_group_id: filters?.restaurantGroupId,
      restaurant_sub_group_id: filters?.restaurantSubGroupId,
      restaurant_id: filters?.restaurantIds,
      search: debouncedEmployeeSearch,
    }),
    queryFn: fetchEmployeeList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurantsList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: filters?.restaurantGroupId || undefined,
        restaurant_sub_group_id: filters?.restaurantSubGroupId || undefined
      }
    })
    return response?.data?.map(normalizeRestaurant) || []
  }

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurantGroupId,
      restaurant_sub_group_id: filters?.restaurantSubGroupId,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurantsList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime:  10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
    }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurantSubGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_sub_groups_list", {
      params: {
        restaurant_group_id: filters?.restaurantGroupId,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
      },
    });
    return response?.data || [];
  };

  const {
    data: restaurantSubGroups = EMPTY_DROPDOWN_OPTIONS,
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: filters?.restaurantGroupId,
      search: debouncedRestaurantSubGroupSearch,
    }),
    queryFn: fetchRestaurantSubGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: Boolean(filters?.restaurantGroupId),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: employeeMenuRef, targetRef: employeeLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
    isFetchingNextPage: employeesFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
  })

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, restaurants, 'id', ['name'])
    ))
  }, [restaurants])

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, restaurantGroups, 'label', ['value'])
    ))
  }, [restaurantGroups])

  useEffect(() => {
    setRestaurantSubGroupOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, restaurantSubGroups, 'value', ['label'])
    ))
  }, [restaurantSubGroups])

  useEffect(() => {
    setEmployeeOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, employees, 'value', ['label'])
    ))
  }, [employees])

  useEffect(() => {
    if(!debouncedRestaurantSearch && restaurants && restaurants.length === 1) {
      setFilters(prev => ({
        ...prev,
        restaurantIds: [restaurants?.[0]?.id],
        employees: 'All Employees',
      }))
      setFiltersChanged(true)
      checkFetchPermission([restaurants?.[0]?.id])
    }
  }, [debouncedRestaurantSearch, restaurants])

  useEffect(() => {
    if(!debouncedRestaurantGroupSearch && restaurantGroups && restaurantGroups.length === 1) {
      setFilters(prev => ({ ...prev, restaurantGroupId: restaurantGroups?.[0]?.label }))
      setFiltersChanged(true)
    }
  }, [debouncedRestaurantGroupSearch, restaurantGroups])

  const handleEmployeeChange = (event) => {
    const selectedEmployeeId = event.target.value

    setFilters((prev) => ({
      ...prev,
      employees: selectedEmployeeId,
      importedOrderIds: [],
    }))
    setFiltersChanged(true)
  }

  const checkFetchPermission = (fetchRestaurants) => {
    let fetchRestaurantsList = []

    if(superAdmin?.role === 'admin'){
      setFetchPermission(true)
    }

    // List of selected restaurants
    fetchRestaurants?.map((id) => {
      restaurantOptionsCache?.forEach((restaurant) => {
        if(restaurant?.id === id) {
          fetchRestaurantsList.push(restaurant)
        }
      })
    })

    for(const restaurant of fetchRestaurantsList) {
      if(strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${restaurant?.id}`)){
        setFetchPermission(true)
        return
      }
      setFetchPermission(false)
    }
  }

  const hideColumns = async (columns) => {
    try {
      await updateColumns({
        table_name: "orders",
        columns: columns,
      });
      const res = await fetchColumns({ table_name: "orders" });
      updateColumnToggle(res);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFiles) return;

    try {
      const formData = new FormData();
      formData.append("orders_data", selectedFiles[0]);
      formData.append("restaurant_id", 2);
      setLoading(true);
      // Replace with your actual API call
      await uploadFile(formData);

      setUploadModalOpen(false);
      // Optionally refresh orders after upload
      fetchOrdersData();
      setSuccessMessage("Orders imported successfully!");
      setSuccessModalOpen(true);
      setLoading(false);
    } catch (error) {
      console.error("Upload failed:", error);
      setLoading(false);
    }
  };

  const updateColumnToggle = (data) => {
    const hiddenColumns = data
      ?.filter((column) => !column.hidden)
      ?.map((column) => {
        switch (column.column_name) {
          case "order_id":
            return "Order Id";
          case "order_no":
            return "Order #";
          case "no_of_guest":
            return "# of Guests";
          case "discount_amount":
            return "Perq Amount";
          default:
            return camelize(column.column_name);
        }
      });

    const newVisibleColumns = {};
    columnOptions.forEach((col) => {
      newVisibleColumns[col] = hiddenColumns.includes(col);
    });

    setVisibleColumns(newVisibleColumns);
  };

  const formattedOrders = (data) => {
    return data?.map((order) => ({
      order_id: order?.order_id,
      "order_#": order?.order_no,
      order_details: (
        <OrderNumberCell
          orderValue={order?.order_id}
          onView={handleOpen}
        />
      ),
      opened: formatDate(order?.opened, order?.time_zone_abbr),
      '#_of_guests': order?.no_of_guest,
      server: order?.server,
      table: order?.table,
      perq_amount: order?.discount_amount ? `$${Number(order?.discount_amount).toFixed(2)}` : '$0.00',
      amount: `$${Number(order?.amount).toFixed(2) ?? '0.00'}`,
      tax: `$${Number(order?.tax).toFixed(2) ?? '0.00'}`,
      tip: `$${Number(order?.tip).toFixed(2) ?? '0.00'}`,
      total: `$${Number(order?.total).toFixed(2) ?? '0.00'}`,
      voided: formatVoided(order?.voided),
      checks: order?.check_no,
      closed: formatDate(order?.closed, order?.time_zone_abbr),
      dining_area: order?.dining_area,
      dining_options: order?.dining_options,
      duration: formatTime(order?.duration),
      gratuity: `$${Number(order?.gratuity).toFixed(2) ?? '0.00'}`,
      order_source: order?.order_source,
      revenue_center: order?.revenue_center,
      service: order?.service,
      tab_names: order?.tab_names,
      location: order?.location,
      paid: formatDate(order?.paid, order?.time_zone_abbr),
    }));
  };

  const formatVoided = (voided) => {
    switch(voided){
      case 'f':
        return 'False'
      case 't':
        return 'True'
      default:
        return ''
    }
  }

  const formatTime = (seconds) => {
    if(seconds) {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      return [
        hrs.toString().padStart(2, "0"),
        mins.toString().padStart(2, "0"),
        secs.toString().padStart(2, "0"),
      ].join(":");
    }
    else{
      return '00:00:00'
    }
  }

  const filteredEmployees = employees?.filter((employee) => {
    return matchesNormalizedSearch(employee?.label, employeeSearch);
  });

  const filteredRestaurants = restaurants?.filter((restaurant) => {
    return matchesNormalizedSearch(restaurant?.name, restaurantSearch);
  })

  const filteredRestaurantGroups = restaurantGroups?.filter((restaurantGroup) => {
    return matchesNormalizedSearch(restaurantGroup?.value, restaurantGroupSearch);
  });

  const columnOptions = [
    "Location",
    "Order Id",
    "Order #",
    "Order Details",
    "Opened",
    "# of Guests",
    "Server",
    "Table",
    "Perq Amount",
    "Amount",
    "Tax",
    "Tip",
    "Gratuity",
    "Total",
    "Voided",
    "Paid",
    "Closed",
    "Duration",
    "Order Source",
    "Checks",
    "Tab Names",
    "Revenue Center",
    "Dining Area",
    "Service",
    "Dining Options",
  ];

  const renderFormattedValue = (selected, option) => {
    return selected.length > 0
      ? `${option.label}: ${
          selected.includes("Yes") && selected.includes("No")
            ? "All"
            : selected.join(", ")
        }`
      : option.label;
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(1);
  };

  const formattedColumns = (data, visibleCols) => {
    return data
      ?.filter((column) => !visibleCols[column])
      ?.map((column) => {
        switch (column) {
          case "Order Id":
            return "order_id";
          case "Order #":
            return "order_no";
          case "# of Guests":
            return "no_of_guest";
          default:
            return snakeCase(column);
        }
      });
  };

  const applyVisibleColumns = (nextVisibleColumns) => {
    setVisibleColumns(nextVisibleColumns);
    hideColumns(formattedColumns(columnOptions, nextVisibleColumns));
  };

  const handleColumnsMenuOpen = (event) => {
    previousVisibleColumnsRef.current = Object.keys(visibleColumns).length > 0
      ? { ...visibleColumns }
      : null;
    setColumnsAnchor(event.currentTarget);
  };

  const handleColumnsMenuClose = () => {
    previousVisibleColumnsRef.current = null;
    setColumnsAnchor(null);
  };

  const handleShowAllColumns = () => {
    const nextVisibleColumns = {};

    columnOptions.forEach((column) => {
      nextVisibleColumns[column] = true;
    });

    applyVisibleColumns(nextVisibleColumns);
  };

  const handleHideAllColumns = () => {
    const nextVisibleColumns = {};

    columnOptions.forEach((column) => {
      nextVisibleColumns[column] = false;
    });

    applyVisibleColumns(nextVisibleColumns);
  };

  const handleColumnToggle = (column, update = true) => {
    setVisibleColumns((prev) => {
      const newVisible = { ...prev, [column]: !prev[column] };
      if (update) {
        const colsToHide = formattedColumns(columnOptions, newVisible);
        hideColumns(colsToHide);
      }
      return newVisible;
    });
  };

  const handleMultiSelectChange = (field) => (e) => {
    checkFetchPermission(e.target.value)
    const values = e.target.value;
    setFilters((prev) => ({
      ...prev,
      [field]: values,
      employees: field === 'restaurantIds' ? 'All Employees' : prev.employees,
      importedOrderIds: [],
    }));
    setFiltersChanged(true);
  };

  const handleRestaurantClear = () => {
    setFilters(prev => ({
      ...prev,
      restaurantIds: [],
      employees: 'All Employees',
      importedOrderIds: []
    }))
    setFiltersChanged(true);
  }

  const handleRestaurantGroupClear = () => {
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setFilters(prev => ({
      ...prev,
      restaurantGroupId: '',
      restaurantSubGroupId: '',
      restaurantIds: [],
      employees: 'All Employees',
      importedOrderIds: []
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupChange = (event) => {
    setRestaurantSearch('')
    setFilters(prev => ({
      ...prev,
      restaurantSubGroupId: event.target.value,
      restaurantIds: [],
      employees: 'All Employees',
      importedOrderIds: []
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupClear = () => {
    setRestaurantSearch('')
    setFilters(prev => ({
      ...prev,
      restaurantSubGroupId: '',
      restaurantIds: [],
      employees: 'All Employees',
      importedOrderIds: []
    }))
    setFiltersChanged(true)
  }

  const handleFilterChange = useCallback((field, value) => {
    // A new group invalidates the sub group list, so drop any pending sub group search.
    if (field === 'restaurantGroupId') {
      setRestaurantSubGroupSearch('')
    }

    setFilters((prev) => {
      if (field === "dateFilter") {
        return {
          ...prev,
          dateFilter: value,
          customDateFrom: null,
          customDateTo: null,
          restaurantIds: prev.restaurantIds,
          employees: prev.employees,
          importedOrderIds: [],
        };
      }

      if (field === "customDateFrom") {
        return {
          ...prev,
          customDateFrom: value,
          customDateTo:
            isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
          restaurantIds: prev.restaurantIds,
          employees: prev.employees,
          importedOrderIds: [],
        };
      }

      return {
        ...prev,
        [field]: value,
        restaurantSubGroupId: field === 'restaurantGroupId' ? '' : prev.restaurantSubGroupId,
        restaurantIds: field === 'restaurantGroupId' ? [] : prev.restaurantIds,
        employees: field === 'restaurantGroupId' ? 'All Employees' : prev.employees,
        importedOrderIds: [],
      };
    });
    setFiltersChanged(true);
  }, []);

  const handleReset = () => {
    setFiltersChanged(false)
    setFilters(defaultFilters)
    setQueryFilter(defaultFilters)
    setSearch("")
    setItemsPerPage(25);
    setPage(1);
    setOrderBy('')
    setOrder('desc')
  }

  const handleRestoreColumn = () => {
    if (!previousVisibleColumnsRef.current) {
      return;
    }

    applyVisibleColumns(previousVisibleColumnsRef.current);
  }

  const { mutateAsync: fetchOrdersMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/orders/fetch_orders', payload),
    invalidateKeys: [...reportMutationKeyGroups.fetchOrders],
    errorMessage: () => null,
    onSuccess: (data, variables) => {
      setPage(1)
    },
    onError: (error, variables) => {
      console.log(error, `Error`)
    },
    onSettled: () => {
      invalidateAuditLogQueries(queryClient);
    }
  });

  const fetchData = async () => {
    let fetchRestaurants = filters?.restaurantIds || []
    let fetchRestaurantsList = []
    const dateRange = getDateRange(filters)

    if(fetchRestaurants.length === 0 ){
      showNotification('Select a restaurant', 'error')
      return
    }

    if (!dateRange) {
      showNotification('Select a valid date range', 'error')
      return
    }

    fetchRestaurants?.map((id) => {
      restaurantOptionsCache?.forEach((restaurant) => {
        if(restaurant?.id === id) {
          fetchRestaurantsList.push(restaurant)
        }
      })
    })

    const fetchDetails = async (restaurant, startDate, endDate) => {
      const restaurantName = restaurant?.name;
      try {
        await fetchOrdersMutation(
          {
            restaurant_id: restaurant?.id,
            api: true,
            start_date: startDate,
            end_date: endDate
          },
          {
            onSuccess: () => {
              const name = restaurantName || 'selected restaurant';
              showNotification(`Orders fetched successfully for ${name}`, 'success');
            },
            onError: (error) => {
              const name = restaurantName || 'selected restaurant';
              showNotification(`Failed to fetch orders for ${name}`, 'error');
            }
          }
        );
      } catch {
        // Handled in onError
      }
    }

    // Processes each restaurant one by one
    let noPermissionRestaurant = []
    for(const restaurant of fetchRestaurantsList) {
      setFetchingLoader(true)
      if (superAdmin?.role === 'admin' || strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${restaurant?.id}`)) {
        // pad the UTC end bound by a day so the full local business day is covered
        // regardless of the restaurant's timezone offset (mirrors the pre-refactor window)
        const paddedEndDate = dayjs(dateRange.to).add(1, 'day').format('YYYY-MM-DD');
        await fetchDetails(restaurant, `${dateRange.from}T00:00:00.000Z`, `${paddedEndDate}T23:59:59.000Z`);
        setFetchingLoader(false)
      }
      else {
        console.log(`You don't have fetch permissions for ${restaurant?.name}`);
        noPermissionRestaurant.push(`${restaurant?.name}`)
        setFetchingLoader(false)
      }
    }
    if(noPermissionRestaurant?.length > 0){
      setTimeout(() => {
        showNotification(`You don't have fetch permissions for ${noPermissionRestaurant.toString()}`, 'error')
      }, [5000])
    }
  };

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = (order_id) => {
    setOpen(true)
    setSelectedOrder(order_id)
  }

  const handleOwnersTableDiscount = (e) => {
    setFilters((prev) => ({ ...prev, has_ot_discount: e.target.value, importedOrderIds: [] }));
    setFiltersChanged(true);
  }

  // Reuses formattedFilters(filters) so the export honors the same filters as the list;
  // sorting is intentionally not sent (exports are always newest-first).
  const { startExport: handleOrderCsvExport, isExporting: csvLoading } = useCsvExport({
    report: 'orders',
    getFilters: () => ({ ...formattedFilters(filters), search: debouncedSearchTerm }),
    fileName: `order_${dayjs(new Date()).format('YYYY-MM-DD')}.csv`,
    label: 'Order Report',
  });

  const totalCount = Math.ceil((ordersPageCount || 0) / itemsPerPage);
  const from = totalCount === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const to = Math.min(page * itemsPerPage, ordersPageCount || 0);

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color={"#515152"}>Reports</StyledLink>
          <StyledLink color={"#515152"}>Sales</StyledLink>
          <StyledLink color={"#515152"}>Orders</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <Box>
        {/* Below grid Contains the Filter sections */}

          <FilterSection
            filters={filters}
            handleFilterChange={handleFilterChange}
            moreOptionsConfig={moreOptionsConfig}
            selectOptions={selectOptions}
            handleMultiSelectChange={handleMultiSelectChange}
            employeeSearch={employeeSearch}
            setEmployeeSearch={setEmployeeSearch}
            employees={employees}
            employeeFetching={employeeFetching}
            employeesFetchingNextPage={employeesFetchingNextPage}
            hasNextEmployeesPage={hasNextEmployeesPage}
            employeeMenuRef={employeeMenuRef}
            employeeLoaderRef={employeeLoaderRef}
            restaurants={restaurants}
            restaurantFetching={restaurantFetching}
            restaurantsFetchingNextPage={restaurantsFetchingNextPage}
            hasNextRestaurantsPage={hasNextRestaurantsPage}
            restaurantMenuRef={restaurantMenuRef}
            restaurantLoaderRef={restaurantLoaderRef}
            allRestaurants={restaurantOptionsCache}
            restaurantGroups={restaurantGroups}
            restaurantGroupFetching={restaurantGroupFetching}
            restaurantGroupsFetchingNextPage={restaurantGroupsFetchingNextPage}
            hasNextRestaurantGroupsPage={hasNextRestaurantGroupsPage}
            restaurantGroupMenuRef={restaurantGroupMenuRef}
            restaurantGroupLoaderRef={restaurantGroupLoaderRef}
            allRestaurantGroups={restaurantGroupOptionsCache}
            showRestaurantSubGroupFilter
            restaurantSubGroups={restaurantSubGroups}
            restaurantSubGroupFetching={restaurantSubGroupFetching}
            hasNextRestaurantSubGroupsPage={hasNextRestaurantSubGroupsPage}
            restaurantSubGroupMenuRef={restaurantSubGroupMenuRef}
            restaurantSubGroupLoaderRef={restaurantSubGroupLoaderRef}
            allRestaurantSubGroups={restaurantSubGroupOptionsCache}
            setRestaurantSubGroupSearch={setRestaurantSubGroupSearch}
            handleRestaurantSubGroupChange={handleRestaurantSubGroupChange}
            handleRestaurantSubGroupClear={handleRestaurantSubGroupClear}
            renderFormattedValue={renderFormattedValue}
            setFilters={setFilters}
            setFiltersChanged={setFiltersChanged}
            resetFilters={handleReset}
            fetchingDataLoader={ordersLoading || fetchingDataLoader}
            fetchData={fetchOrdersData}
            filtersChanged={filtersChanged}
            setQueryFilter={setQueryFilter}
            setUpdateData={() => {}}
            restaurantSearch={restaurantSearch}
            setRestaurantSearch={setRestaurantSearch}
            restaurantGroupSearch={restaurantGroupSearch}
            setRestaurantGroupSearch={setRestaurantGroupSearch}
            handleOwnersTableDiscount={handleOwnersTableDiscount}
            handleRestaurantClear={handleRestaurantClear}
            handleRestaurantGroupClear={handleRestaurantGroupClear}
            handleEmployeeChange={handleEmployeeChange}
            employeeSelectValue={filters?.employees}
            employeeDisplayValue={getEmployeeName(filters?.employees)}
            setOrderBy={setOrderBy}
            setOrder={setOrder}
            setPage={setPage}
          />

        <MenuHeading
          report={false}
          dividerStyle={{ marginBottom: '16px' }}
          rightComponent={
            <StyledSearchBar
              value={search}
              placeholder="Search by Order #"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => handleSearch(e.target.value)}
              autoComplete="off"
              inputProps={{ 'data-testid': 'orders-search-input' }}
            />
          }
        >
          Orders
        </MenuHeading>

        {/* When requirement gets confirmed below code will be uncommented or removed */}
        {/* <div style={{ padding: '10px' }}>
          <OrderStatisticsCards
            data={hasAnalyticsDateRange ? orderAnalyticsData?.summaryData3 : {}}
            loading={hasAnalyticsDateRange ? orderAnalyticsLoading : false}
            isMobile={isMobile}
          />

          <OrderTotalSalesComponent
            totalSalesLoading={hasAnalyticsDateRange ? orderAnalyticsLoading : false}
            summaryData1={hasAnalyticsDateRange ? orderAnalyticsData?.summaryData1 : {}}
            summaryData2={hasAnalyticsDateRange ? orderAnalyticsData?.summaryData2 : {}}
            totalSalesData={hasAnalyticsDateRange ? orderAnalyticsData?.totalSales : []}
            isMobile={isMobile}
          />
        </div> */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            marginTop: '0px'
          }}
        >

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Select
            data-testid="orders-per-page-select"
            SelectDisplayProps={{
              "data-testid": "orders-per-page-select-display",
            }}
            value={itemsPerPage}
            size="small"
            sx={perPageStyles}
            onChange={(e) => {
              setItemsPerPage(e.target.value)
              setPage(1)
            }}
          >
            {[10, 25, 50, 100].map((num) => (
              <StyledMenuItem key={num} value={num} data-testid={`orders-per-page-option-${num}`}>
                {num}
              </StyledMenuItem>
            ))}
          </Select>
          <StyledTypography
            data-testid="orders-pagination-summary"
            variant="body2"
          >
            entries per page
          </StyledTypography>
        </div>


          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <React.Fragment>
              <StyledTypography
                data-testid="orders-results-summary"
                variant="body2"
                sx={{
                  color: "black",
                  fontSize: "0.875rem",
                }}
              >
                Showing {from} to{" "}
                {to} of {ordersPageCount || 0} orders
              </StyledTypography>

              <ShowHideButton
                endIcon={<ArrowDropDownIcon />}
                variant="outlined"
                onClick={handleColumnsMenuOpen}
              >
                Show / hide columns
              </ShowHideButton>
              <Menu
                anchorEl={columnsAnchor}
                open={Boolean(columnsAnchor)}
                onClose={handleColumnsMenuClose}
                PaperProps={{ sx: { maxHeight: 600 } }}
              >
                <StyledMenuItem
                  onClick={handleShowAllColumns}
                >
                  Show all
                </StyledMenuItem>
                <StyledMenuItem
                  onClick={handleHideAllColumns}
                >
                  Show none
                </StyledMenuItem>
                <StyledMenuItem
                  onClick={handleRestoreColumn}
                >
                  Restore
                </StyledMenuItem>
                <Divider />
                {columnOptions.map((column) => (
                  <StyledMenuItem key={column} dense>
                    <Checkbox
                      checked={visibleColumns[column] || false}
                      onChange={() => {
                        handleColumnToggle(column);
                      }}
                      size="small"
                    />
                    <Typography variant="body2">{column}</Typography>
                  </StyledMenuItem>
                ))}
              </Menu>
            </React.Fragment>

            {/* When requirement gets confirmed below code will be uncommented or removed */}
            {/* <UploadModal
              setUploadModalOpen={setUploadModalOpen}
              successModalOpen={successModalOpen}
              setSuccessModalOpen={setSuccessModalOpen}
              uploadModalOpen={uploadModalOpen}
              getRootProps={getRootProps}
              isDragActive={isDragActive}
              getInputProps={getInputProps}
              selectedFiles={selectedFiles}
              formatFileSize={formatFileSize}
              setSelectedFiles={setSelectedFiles}
              handleFileUpload={handleFileUpload}
              loading={loading}
              element={"Orders"}
              successMessage={successMessage}
              uploadPermission={fetchPermission}
            /> */}

            <Button
              sx={{
                backgroundColor: "#2b4fb9",
                color: "white",
                "&:hover": { backgroundColor: "#1a3a7a" },
                "&.Mui-disabled": { backgroundColor: "#f0f0f0" },
              }}
              onClick={fetchData}
              component="label"
              disabled={!fetchPermission || fetchingLoader}
            >
              {fetchingLoader ? (
                <LoadingSpinner isLoading={true}/>
              ) : (
                <CachedIcon />
              )}
            </Button>

            {
              csvLoading ? (
                  <IconButton disableRipple data-testid="export-csv-loading">
                    <img style={{ height: 24 }} src={csvloader} alt="loading..." />
                  </IconButton>
              ) : (
                <IconButton
                  onClick={handleOrderCsvExport}
                  disabled={sortedOrders?.length === 0 || csvLoading}
                  sx={{color: '#2b4fb9'}}
                  data-testid="export-csv-button"
                >
                  <DownloadIcon />
                </IconButton>
              )
            }

          </div>
        </Box>

        <DataTable
          fetchingDataLoader={ordersLoading && !debouncedSearchTerm}
          searchLoading={ordersLoading && !!debouncedSearchTerm}
          hasSearchTerm={Boolean(debouncedSearchTerm)}
          columnOptions={columnOptions}
          formattedOrders={formattedOrders}
          sortedOrders={sortedOrders}
          orderBy={orderBy}
          order={order}
          handleSort={handleSort}
          visibleColumns={visibleColumns}
          loadingImg={loadingImg}
          totalCount={totalCount}
          tableId={"orders"}
          handleOpen={handleOpen}
          page={page}
          setPage={setPage}
          nonSortableColumns={["Order Details"]}
          emptyStateText="No Data Exists"
        />

        {/* Order Detail Modal */}
        <OrderAppliedDiscountModal
          open={open}
          handleClose={handleClose}
          ordersData={orderData}
          isLoading={appliedDiscountLoading}
        />
      </Box>
    </Box>
  );
};

export default ReportHomePage;
